import {PkLitElement} from "./abstract-element.js";
import {html, css, ref, createRef} from "../libs/lit.dist.js";
import {cancelDomEvent} from "../util/browser.js";

let inputId = 1;

export class PkToggle extends PkLitElement {

    inputId;
    inputRef = createRef();

    static formAssociated = true;

    static styles = css`
        * {
            box-sizing: border-box;
        }
        
        :host {
            box-sizing: border-box;
            --color: var(--gray-500);
            --accent: var(--primary);
            --background: var(--white);
            --toggle-color: var(--color);
            --border-width: 2px;
            --toggle-gap-y: 4px;
            --toggle-gap-x: 2px;
            --gap: 0.5rem;
            --width: 2rem;
            --height: 1.125rem;
            --toggle-size: calc(var(--height) - (var(--toggle-gap-y) * 2));
            display: inline-flex;
            gap: var(--gap);
            margin: 0;
            padding: 0;
            align-items: baseline;
        }
        
        input {
            flex-shrink: 0;
            top: 0.123em;
            margin: 0;
            padding: 0;
            vertical-align: top;
            appearance: none;
            background-color: var(--background);
            border-radius: 2em;
            width: var(--width);
            height: var(--height);
            border: var(--border-width) solid var(--color);
            position: relative;
            transition: all 0.15s ease-in-out;
            
            &::after {
                content: '';
                background-color: var(--toggle-color);
                position: absolute;
                top: 50%;
                left: var(--toggle-gap-x);
                translate: 0 -50%;
                width: var(--toggle-size);
                height: var(--toggle-size);
                border-radius: 50%;
                display: block;
                transition: all 0.15s ease-in-out;
            }
            
            &:checked::after {
                left: calc(var(--width) - var(--toggle-size) - (2 * var(--toggle-gap-x)));
                --toggle-color: var(--white);
            }
            
            &:checked {
                --background: var(--accent);
                --color: var(--accent);
            }
            
            &:disabled {
                --background: var(--gray-200);
                --color: var(--gray-400);
                --toggle-color: var(--gray-400);
            }            
            
            &:disabled::after {
                --toggle-color: var(--gray-400);
            }
             
            &:focus {
                --toggle-color: var(--color);
                box-shadow: 0 0 0 0.25rem var(--bs-focus-ring-color);
            }
            
        }
    
        :host(.align-end) {
            flex-direction: row-reverse;
        }
        
        :host(.align-end.text-start) .label,
        :host(.align-end.text-left) .label {
            margin-right: auto;
        }
    `;

    static properties = {
        type: {type: String},
        name: {type: String},
        checked: {type: Boolean, reflect: true},
        disabled: {type: Boolean},
        value: {type: String},
        url: {type: String},
        isToggleRequested: {type: Boolean, state: true},
    };

    constructor() {
        super();
        this.inputId = inputId++;
        this.internals = this.attachInternals();
        this.value = 'On';
    }

    connectedCallback() {
        super.connectedCallback();
        this.updateFromValue();
    }

    async onChangeHandler() {
        if (!this.disabled) {
            if (this.dispatchEvent(new Event('beforechange', {bubbles: true, cancelable: true})) === false) {
                this.inputRef.value.checked = this.checked;
                return;
            }
            this.checked = this.inputRef.value.checked;
            this.dispatchEvent(new Event('input', {bubbles: true}));
            this.dispatchEvent(new Event('change', {bubbles: true}));
        }
    }

    updateFromValue() {
        this.internals.setFormValue(this.checked && !this.disabled ? this.value : undefined);
    }

    update(t) {
        super.update(t);
        if (t.has('checked') || t.has('disabled')) {
            this.updateFromValue();
        }
    }

    render() {
        return html`
            <input
                ${ref(this.inputRef)}
                type="checkbox"
                role="switch"
                id="pk-live-checkbox-internal-${this.inputId}"
                aria-label="${this.title || ''}"
                .checked="${this.checked}"
                .disabled="${this.disabled || this.isToggleRequested}"
                @change="${cancelDomEvent(e => this.onChangeHandler(e))}"
            >
            <div class="label">
                <label for="pk-live-checkbox-internal-${this.inputId}">
                    <slot></slot>
                </label>
                <div>
                    <slot name="description"></slot>
                </div>
            </div>
        `;
    }
}

customElements.define('pk-toggle', PkToggle);
